import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Divider
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import {
  updateScreenTitle,
  showSuccessMessage,
  showLoader,
  dismissLoader
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import WDButton from '../../../../MaterialComponents/WDButton';
import pxToRem from '../../../../Themes/Functions/pxToRem';
import RecordOfflineTransactionDialog from './RecordOfflineTransactionDialog';
import PendingVerificationDashboard from './PendingVerificationDashboard';

// ============================================
// MOCK DATA
// ============================================

const MOCK_WALLET_BALANCE = {
  currentBalance: 125750.50,
  totalReceived: 450000.00,
  totalPaidToPartners: 180000.00,
  totalCommissionsPaid: 35000.00,
  totalExpenses: 12500.00,
  bankBalance: 122500.00, // Should match after reconciliation
  lastReconciled: '2025-10-19T10:30:00'
};

const MOCK_TRANSACTIONS = [
  {
    id: 'txn-001',
    date: '2025-10-20',
    type: 'RECEIVE_FROM_CUSTOMER',
    description: 'Payment from Tech Innovations LLC - Visa Renewal',
    customer: 'Tech Innovations LLC',
    ticketNumber: 'TKT-2024-1234',
    amount: 3500.00,
    paymentMethod: 'Bank Transfer',
    reference: 'BT-20251020-001',
    status: 'COMPLETED'
  },
  {
    id: 'txn-002',
    date: '2025-10-19',
    type: 'PAY_SERVICE_PARTNER',
    description: 'Payment to Al Sada Typing Center - Service Completion',
    partner: 'Al Sada Typing Center',
    ticketNumber: 'TKT-2024-1230',
    amount: -2500.00,
    paymentMethod: 'Bank Transfer',
    reference: 'PAY-SP-20251019-001',
    status: 'COMPLETED'
  },
  {
    id: 'txn-003',
    date: '2025-10-19',
    type: 'PAY_CHANNEL_PARTNER',
    description: 'Commission to Elite Business Consultants',
    partner: 'Elite Business Consultants',
    ticketCount: 5,
    amount: -1250.00,
    paymentMethod: 'Bank Transfer',
    reference: 'PAY-CP-20251019-001',
    status: 'COMPLETED'
  },
  {
    id: 'txn-004',
    date: '2025-10-18',
    type: 'EXPENSE',
    description: 'Office Rent - October 2025',
    category: 'Rent',
    amount: -8000.00,
    paymentMethod: 'Bank Transfer',
    reference: 'EXP-20251018-001',
    status: 'COMPLETED'
  },
  {
    id: 'txn-005',
    date: '2025-10-18',
    type: 'BANK_DEPOSIT',
    description: 'Cash deposit to Emirates NBD',
    bankAccount: 'Emirates NBD - Account ending in 1234',
    amount: 5000.00,
    reference: 'DEP-20251018-001',
    status: 'COMPLETED'
  },
  {
    id: 'txn-006',
    date: '2025-10-17',
    type: 'BANK_WITHDRAWAL',
    description: 'Cash withdrawal for petty cash',
    bankAccount: 'Emirates NBD - Account ending in 1234',
    amount: -1000.00,
    reference: 'WD-20251017-001',
    status: 'COMPLETED'
  }
];

const MOCK_PENDING_PARTNER_PAYMENTS = [
  {
    id: 'ppp-001',
    partner: 'Al Sada Typing Center',
    ticketCount: 3,
    totalAmount: 4500.00,
    tickets: [
      { ticketNumber: 'TKT-2024-1240', service: 'Trade License Renewal', amount: 2500.00, completedDate: '2025-10-19' },
      { ticketNumber: 'TKT-2024-1241', service: 'Visa Application', amount: 1200.00, completedDate: '2025-10-19' },
      { ticketNumber: 'TKT-2024-1242', service: 'Emirates ID', amount: 800.00, completedDate: '2025-10-20' }
    ]
  },
  {
    id: 'ppp-002',
    partner: 'Dubai Typing Services',
    ticketCount: 2,
    totalAmount: 3200.00,
    tickets: [
      { ticketNumber: 'TKT-2024-1243', service: 'PRO Services', amount: 2000.00, completedDate: '2025-10-20' },
      { ticketNumber: 'TKT-2024-1244', service: 'Document Attestation', amount: 1200.00, completedDate: '2025-10-20' }
    ]
  }
];

const MOCK_PENDING_COMMISSION_PAYMENTS = [
  {
    id: 'pcp-001',
    channelPartner: 'Elite Business Consultants',
    ticketCount: 4,
    totalCommission: 2800.00,
    tickets: [
      { ticketNumber: 'TKT-2024-1245', customer: 'Tech Innovations LLC', service: 'Visa Renewal', serviceCharge: 200, commissionRate: '25%', commission: 50 },
      { ticketNumber: 'TKT-2024-1246', customer: 'Gulf Trading Co.', service: 'Trade License', serviceCharge: 800, commissionRate: 'AED 150', commission: 150 },
      { ticketNumber: 'TKT-2024-1247', customer: 'Tech Innovations LLC', service: 'Emirates ID', serviceCharge: 150, commissionRate: '20%', commission: 30 },
      { ticketNumber: 'TKT-2024-1248', customer: 'Digital Solutions DMCC', service: 'Business Setup', serviceCharge: 2000, commissionRate: '22%', commission: 440 }
    ]
  },
  {
    id: 'pcp-002',
    channelPartner: 'Gulf Marketing Agency',
    ticketCount: 2,
    totalCommission: 720.00,
    tickets: [
      { ticketNumber: 'TKT-2024-1249', customer: 'Marketing Pros', service: 'Visa Renewal', serviceCharge: 200, commissionRate: '18%', commission: 36 },
      { ticketNumber: 'TKT-2024-1250', customer: 'Marketing Pros', service: 'PRO Services', serviceCharge: 500, commissionRate: '15%', commission: 75 }
    ]
  }
];

const MOCK_BANK_ACCOUNTS = [
  { id: 'bank-001', name: 'Emirates NBD', accountNumber: '****1234', balance: 85000.00, currency: 'AED' },
  { id: 'bank-002', name: 'ADIB', accountNumber: '****5678', balance: 37500.00, currency: 'AED' }
];

const MOCK_EXPENSE_CATEGORIES = [
  'Rent',
  'Salaries',
  'Utilities',
  'Marketing',
  'Office Supplies',
  'Software & Subscriptions',
  'Professional Fees',
  'Transportation',
  'Communication',
  'Maintenance',
  'Other'
];

// ============================================
// COMPONENTS
// ============================================

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle, bgColor }) => (
  <Card sx={{ height: '100%', bgcolor: bgColor || 'background.paper' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: `${color}.main`, my: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.lighter`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 28, color: `${color}.main` }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ============================================
// MAIN COMPONENT
// ============================================

const ThaklisMasterWallet = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isMockMode, setIsMockMode] = useState(true);

  // Wallet data
  const [walletBalance, setWalletBalance] = useState(MOCK_WALLET_BALANCE);
  const [transactions, setTransactions] = useState(MOCK_TRANSACTIONS);
  const [pendingPartnerPayments, setPendingPartnerPayments] = useState(MOCK_PENDING_PARTNER_PAYMENTS);
  const [pendingCommissions, setPendingCommissions] = useState(MOCK_PENDING_COMMISSION_PAYMENTS);
  const [bankAccounts, setBankAccounts] = useState(MOCK_BANK_ACCOUNTS);

  // Dialog states
  const [payPartnerDialogOpen, setPayPartnerDialogOpen] = useState(false);
  const [payCommissionDialogOpen, setPayCommissionDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [bankTransferDialogOpen, setBankTransferDialogOpen] = useState(false);
  const [offlineTransactionDialogOpen, setOfflineTransactionDialogOpen] = useState(false);

  // Form states
  const [selectedPartnerPayment, setSelectedPartnerPayment] = useState(null);
  const [selectedCommissionPayment, setSelectedCommissionPayment] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    category: '',
    amount: '',
    paymentMethod: 'Bank Transfer',
    reference: ''
  });
  const [bankTransferForm, setBankTransferForm] = useState({
    type: 'DEPOSIT',
    bankAccount: '',
    amount: '',
    description: '',
    reference: ''
  });

  useEffect(() => {
    dispatch(updateScreenTitle('Thaklis Master Wallet'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'RECEIVE_FROM_CUSTOMER': 'Customer Payment',
      'PAY_SERVICE_PARTNER': 'Service Partner Payment',
      'PAY_CHANNEL_PARTNER': 'Channel Partner Commission',
      'EXPENSE': 'Expense',
      'BANK_DEPOSIT': 'Bank Deposit',
      'BANK_WITHDRAWAL': 'Bank Withdrawal'
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'RECEIVE_FROM_CUSTOMER': 'success',
      'PAY_SERVICE_PARTNER': 'error',
      'PAY_CHANNEL_PARTNER': 'warning',
      'EXPENSE': 'error',
      'BANK_DEPOSIT': 'info',
      'BANK_WITHDRAWAL': 'error'
    };
    return colors[type] || 'default';
  };

  // ============================================
  // HANDLERS
  // ============================================

  const handlePayPartner = (payment) => {
    setSelectedPartnerPayment(payment);
    setPayPartnerDialogOpen(true);
  };

  const handleConfirmPartnerPayment = () => {
    dispatch(showLoader());
    setTimeout(() => {
      const newTransaction = {
        id: `txn-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: 'PAY_SERVICE_PARTNER',
        description: `Payment to ${selectedPartnerPayment.partner} - ${selectedPartnerPayment.ticketCount} tickets`,
        partner: selectedPartnerPayment.partner,
        ticketCount: selectedPartnerPayment.ticketCount,
        amount: -selectedPartnerPayment.totalAmount,
        paymentMethod: 'Bank Transfer',
        reference: `PAY-SP-${Date.now()}`,
        status: 'COMPLETED'
      };

      setTransactions([newTransaction, ...transactions]);
      setPendingPartnerPayments(pendingPartnerPayments.filter(p => p.id !== selectedPartnerPayment.id));
      setWalletBalance({
        ...walletBalance,
        currentBalance: walletBalance.currentBalance - selectedPartnerPayment.totalAmount,
        totalPaidToPartners: walletBalance.totalPaidToPartners + selectedPartnerPayment.totalAmount
      });

      dispatch(showSuccessMessage(`Payment of ${formatCurrency(selectedPartnerPayment.totalAmount)} to ${selectedPartnerPayment.partner} completed (Mock)`));
      setPayPartnerDialogOpen(false);
      dispatch(dismissLoader());
    }, 500);
  };

  const handlePayCommission = (payment) => {
    setSelectedCommissionPayment(payment);
    setPayCommissionDialogOpen(true);
  };

  const handleConfirmCommissionPayment = () => {
    dispatch(showLoader());
    setTimeout(() => {
      const newTransaction = {
        id: `txn-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: 'PAY_CHANNEL_PARTNER',
        description: `Commission to ${selectedCommissionPayment.channelPartner} - ${selectedCommissionPayment.ticketCount} tickets`,
        partner: selectedCommissionPayment.channelPartner,
        ticketCount: selectedCommissionPayment.ticketCount,
        amount: -selectedCommissionPayment.totalCommission,
        paymentMethod: 'Bank Transfer',
        reference: `PAY-CP-${Date.now()}`,
        status: 'COMPLETED'
      };

      setTransactions([newTransaction, ...transactions]);
      setPendingCommissions(pendingCommissions.filter(p => p.id !== selectedCommissionPayment.id));
      setWalletBalance({
        ...walletBalance,
        currentBalance: walletBalance.currentBalance - selectedCommissionPayment.totalCommission,
        totalCommissionsPaid: walletBalance.totalCommissionsPaid + selectedCommissionPayment.totalCommission
      });

      dispatch(showSuccessMessage(`Commission of ${formatCurrency(selectedCommissionPayment.totalCommission)} to ${selectedCommissionPayment.channelPartner} paid (Mock)`));
      setPayCommissionDialogOpen(false);
      dispatch(dismissLoader());
    }, 500);
  };

  const handleAddExpense = () => {
    setExpenseForm({
      description: '',
      category: '',
      amount: '',
      paymentMethod: 'Bank Transfer',
      reference: ''
    });
    setExpenseDialogOpen(true);
  };

  const handleSaveExpense = () => {
    if (!expenseForm.description || !expenseForm.category || !expenseForm.amount) {
      return;
    }

    dispatch(showLoader());
    setTimeout(() => {
      const newTransaction = {
        id: `txn-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: 'EXPENSE',
        description: expenseForm.description,
        category: expenseForm.category,
        amount: -parseFloat(expenseForm.amount),
        paymentMethod: expenseForm.paymentMethod,
        reference: expenseForm.reference || `EXP-${Date.now()}`,
        status: 'COMPLETED'
      };

      setTransactions([newTransaction, ...transactions]);
      setWalletBalance({
        ...walletBalance,
        currentBalance: walletBalance.currentBalance - parseFloat(expenseForm.amount),
        totalExpenses: walletBalance.totalExpenses + parseFloat(expenseForm.amount)
      });

      dispatch(showSuccessMessage(`Expense of ${formatCurrency(expenseForm.amount)} recorded (Mock)`));
      setExpenseDialogOpen(false);
      dispatch(dismissLoader());
    }, 500);
  };

  const handleBankTransfer = () => {
    setBankTransferForm({
      type: 'DEPOSIT',
      bankAccount: '',
      amount: '',
      description: '',
      reference: ''
    });
    setBankTransferDialogOpen(true);
  };

  const handleSaveBankTransfer = () => {
    if (!bankTransferForm.bankAccount || !bankTransferForm.amount || !bankTransferForm.description) {
      return;
    }

    dispatch(showLoader());
    setTimeout(() => {
      const amount = parseFloat(bankTransferForm.amount);
      const isDeposit = bankTransferForm.type === 'DEPOSIT';

      const newTransaction = {
        id: `txn-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: isDeposit ? 'BANK_DEPOSIT' : 'BANK_WITHDRAWAL',
        description: bankTransferForm.description,
        bankAccount: bankAccounts.find(b => b.id === bankTransferForm.bankAccount)?.name,
        amount: isDeposit ? amount : -amount,
        reference: bankTransferForm.reference || `${isDeposit ? 'DEP' : 'WD'}-${Date.now()}`,
        status: 'COMPLETED'
      };

      setTransactions([newTransaction, ...transactions]);
      setWalletBalance({
        ...walletBalance,
        currentBalance: isDeposit ? walletBalance.currentBalance + amount : walletBalance.currentBalance - amount
      });

      dispatch(showSuccessMessage(`${isDeposit ? 'Deposit' : 'Withdrawal'} of ${formatCurrency(amount)} recorded (Mock)`));
      setBankTransferDialogOpen(false);
      dispatch(dismissLoader());
    }, 500);
  };

  const totalPendingPartnerPayments = pendingPartnerPayments.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalPendingCommissions = pendingCommissions.reduce((sum, p) => sum + p.totalCommission, 0);
  const totalBankBalance = bankAccounts.reduce((sum, b) => sum + b.balance, 0);

  return (
    <Box sx={{ py: 1 }}>
      {isMockMode && (
        <Alert severity="warning" icon={<Icon>info</Icon>} sx={{ mb: 2 }}>
          <strong>Mock Mode:</strong> This is a demo of the Thaklis Master Wallet. Real API integration pending.
        </Alert>
      )}

      {/* Wallet Balance Overview */}
      <Card sx={{ mb: 3, p: 3, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Thaklis Master Wallet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage all money flow, payments, and accounting
            </Typography>
          </Box>
          <MockDataIndicator isMock={isMockMode} />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Current Wallet Balance"
              value={formatCurrency(walletBalance.currentBalance)}
              icon={WalletIcon}
              color="primary"
              subtitle="Available funds"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Total Bank Balance"
              value={formatCurrency(totalBankBalance)}
              icon={AccountBalanceIcon}
              color="info"
              subtitle={`${bankAccounts.length} bank accounts`}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Pending Partner Payments"
              value={formatCurrency(totalPendingPartnerPayments)}
              icon={BusinessIcon}
              color="warning"
              subtitle={`${pendingPartnerPayments.length} pending`}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Pending Commissions"
              value={formatCurrency(totalPendingCommissions)}
              icon={ReceiptIcon}
              color="error"
              subtitle={`${pendingCommissions.length} pending`}
            />
          </Grid>
        </Grid>

        {/* Quick Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <WDButton
            variant="gradient"
            color="error"
            startIcon={<Icon>payments</Icon>}
            onClick={() => setTabValue(1)}
            sx={{ fontSize: '0.875rem', py: 1, px: 2 }}
          >
            Pay Service Partners ({pendingPartnerPayments.length})
          </WDButton>
          <WDButton
            variant="gradient"
            color="warning"
            startIcon={<Icon>account_balance_wallet</Icon>}
            onClick={() => setTabValue(2)}
            sx={{ fontSize: '0.875rem', py: 1, px: 2 }}
          >
            Pay Commissions ({pendingCommissions.length})
          </WDButton>
          <WDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add_circle</Icon>}
            onClick={handleAddExpense}
            sx={{ fontSize: '0.875rem', py: 1, px: 2 }}
          >
            Record Expense
          </WDButton>
          <WDButton
            variant="gradient"
            color="success"
            startIcon={<Icon>account_balance</Icon>}
            onClick={handleBankTransfer}
            sx={{ fontSize: '0.875rem', py: 1, px: 2 }}
          >
            Bank Transfer
          </WDButton>
          <WDButton
            variant="gradient"
            color="secondary"
            startIcon={<Icon>receipt_long</Icon>}
            onClick={() => setOfflineTransactionDialogOpen(true)}
            sx={{ fontSize: '0.875rem', py: 1, px: 2 }}
          >
            Record Offline Tx
          </WDButton>
        </Box>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 48
            }
          }}
        >
          <Tab label="All Transactions" />
          <Tab label={`Service Partner Payments (${pendingPartnerPayments.length})`} />
          <Tab label={`Commission Payments (${pendingCommissions.length})`} />
          <Tab label="Expenses" />
          <Tab label="Bank Reconciliation" />
          <Tab label="Pending Verification" />
        </Tabs>

        {/* Tab 0: All Transactions */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reference</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow key={txn.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>
                          {new Date(txn.date).toLocaleDateString('en-GB')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTransactionTypeLabel(txn.type)}
                          size="small"
                          color={getTransactionTypeColor(txn.type)}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>
                          {txn.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {txn.reference}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: txn.amount > 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {txn.amount > 0 ? '+' : ''}{formatCurrency(txn.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.status}
                          size="small"
                          color="success"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 1: Service Partner Payments */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            {pendingPartnerPayments.length === 0 ? (
              <Alert severity="info">No pending service partner payments</Alert>
            ) : (
              pendingPartnerPayments.map((payment) => (
                <Card key={payment.id} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {payment.partner}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.ticketCount} completed tickets
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'error.main' }}>
                          {formatCurrency(payment.totalAmount)}
                        </Typography>
                        <WDButton
                          variant="gradient"
                          color="error"
                          size="small"
                          onClick={() => handlePayPartner(payment)}
                          sx={{ mt: 1, py: 0.5, px: 2, fontSize: '0.75rem' }}
                        >
                          Pay Now
                        </WDButton>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.813rem', fontWeight: 600 }}>
                      Ticket Breakdown:
                    </Typography>
                    {payment.tickets.map((ticket, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {ticket.ticketNumber} - {ticket.service}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {formatCurrency(ticket.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Commission Payments */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            {pendingCommissions.length === 0 ? (
              <Alert severity="info">No pending commission payments</Alert>
            ) : (
              pendingCommissions.map((payment) => (
                <Card key={payment.id} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {payment.channelPartner}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.ticketCount} tickets - Commission earned
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'warning.main' }}>
                          {formatCurrency(payment.totalCommission)}
                        </Typography>
                        <WDButton
                          variant="gradient"
                          color="warning"
                          size="small"
                          onClick={() => handlePayCommission(payment)}
                          sx={{ mt: 1, py: 0.5, px: 2, fontSize: '0.75rem' }}
                        >
                          Pay Commission
                        </WDButton>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.813rem', fontWeight: 600 }}>
                      Commission Breakdown:
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Ticket</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Customer</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Service</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Service Charge</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Rate</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Commission</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payment.tickets.map((ticket, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: '0.7rem' }}>{ticket.ticketNumber}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem' }}>{ticket.customer}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem' }}>{ticket.service}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>AED {ticket.serviceCharge}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem' }}>{ticket.commissionRate}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'warning.main' }}>
                                AED {ticket.commission}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Expenses */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Expense Transactions
              </Typography>
              <WDButton
                variant="gradient"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={handleAddExpense}
                size="small"
                sx={{ fontSize: '0.813rem', py: 0.75, px: 2 }}
              >
                Add Expense
              </WDButton>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'info.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.filter(t => t.type === 'EXPENSE').map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>{new Date(txn.date).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{txn.description}</TableCell>
                      <TableCell>
                        <Chip label={txn.category} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                        {formatCurrency(txn.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 4: Bank Reconciliation */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
              Bank Account Balances
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {bankAccounts.map((bank) => (
                <Grid item xs={12} md={6} key={bank.id}>
                  <Card sx={{ border: 1, borderColor: 'divider' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {bank.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Account: {bank.accountNumber}
                          </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                          {formatCurrency(bank.balance)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: 2 }}>
              <WDButton
                variant="gradient"
                color="success"
                startIcon={<Icon>sync_alt</Icon>}
                onClick={handleBankTransfer}
                sx={{ fontSize: '0.813rem', py: 0.75, px: 2 }}
              >
                Record Bank Transfer
              </WDButton>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 0.5 }}>
                Reconciliation Status
              </Typography>
              <Typography variant="caption">
                Wallet Balance: {formatCurrency(walletBalance.currentBalance)} |
                Bank Balance: {formatCurrency(totalBankBalance)} |
                Difference: {formatCurrency(walletBalance.currentBalance - totalBankBalance)}
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
              Bank Transactions
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'success.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bank Account</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.filter(t => t.type === 'BANK_DEPOSIT' || t.type === 'BANK_WITHDRAWAL').map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>{new Date(txn.date).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>
                        <Chip
                          label={txn.type === 'BANK_DEPOSIT' ? 'Deposit' : 'Withdrawal'}
                          size="small"
                          color={txn.type === 'BANK_DEPOSIT' ? 'success' : 'error'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{txn.bankAccount}</TableCell>
                      <TableCell>{txn.description}</TableCell>
                      <TableCell align="right" sx={{ color: txn.amount > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                        {txn.amount > 0 ? '+' : ''}{formatCurrency(txn.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 5: Pending Verification */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 2 }}>
            <PendingVerificationDashboard onRefresh={() => {}} />
          </Box>
        </TabPanel>
      </Card>

      {/* Record Offline Transaction Dialog */}
      <RecordOfflineTransactionDialog
        open={offlineTransactionDialogOpen}
        onClose={() => setOfflineTransactionDialogOpen(false)}
        onSuccess={(result) => {
          dispatch(showSuccessMessage(result.message || 'Transaction recorded'));
        }}
        bankAccounts={bankAccounts.map(b => ({
          id: b.id,
          accountName: b.name,
          bankName: b.name
        }))}
      />

      {/* Pay Partner Dialog */}
      <Dialog open={payPartnerDialogOpen} onClose={() => setPayPartnerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Service Partner Payment</DialogTitle>
        <DialogContent>
          {selectedPartnerPayment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You are about to pay <strong>{selectedPartnerPayment.partner}</strong> for {selectedPartnerPayment.ticketCount} completed tickets.
              </Typography>
              <Box sx={{ bgcolor: 'error.lighter', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                  Amount: {formatCurrency(selectedPartnerPayment.totalAmount)}
                </Typography>
              </Box>
              <Alert severity="info">
                This payment will be recorded in the transaction history and deducted from the wallet balance.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayPartnerDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmPartnerPayment} variant="contained" color="error">
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay Commission Dialog */}
      <Dialog open={payCommissionDialogOpen} onClose={() => setPayCommissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Commission Payment</DialogTitle>
        <DialogContent>
          {selectedCommissionPayment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You are about to pay commission to <strong>{selectedCommissionPayment.channelPartner}</strong> for {selectedCommissionPayment.ticketCount} tickets.
              </Typography>
              <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                  Commission: {formatCurrency(selectedCommissionPayment.totalCommission)}
                </Typography>
              </Box>
              <Alert severity="info">
                This commission will be recorded in the transaction history and deducted from the wallet balance.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayCommissionDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmCommissionPayment} variant="contained" color="warning">
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={expenseDialogOpen} onClose={() => setExpenseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Expense</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    label="Category"
                  >
                    {MOCK_EXPENSE_CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Amount (AED)"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={expenseForm.paymentMethod}
                    onChange={(e) => setExpenseForm({ ...expenseForm, paymentMethod: e.target.value })}
                    label="Payment Method"
                  >
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Credit Card">Credit Card</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reference (Optional)"
                  value={expenseForm.reference}
                  onChange={(e) => setExpenseForm({ ...expenseForm, reference: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenseDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleSaveExpense} variant="contained" color="info" disabled={!expenseForm.description || !expenseForm.category || !expenseForm.amount}>
            Record Expense
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Transfer Dialog */}
      <Dialog open={bankTransferDialogOpen} onClose={() => setBankTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Bank Transfer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Transfer Type</InputLabel>
                  <Select
                    value={bankTransferForm.type}
                    onChange={(e) => setBankTransferForm({ ...bankTransferForm, type: e.target.value })}
                    label="Transfer Type"
                  >
                    <MenuItem value="DEPOSIT">Deposit to Bank</MenuItem>
                    <MenuItem value="WITHDRAWAL">Withdrawal from Bank</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Bank Account</InputLabel>
                  <Select
                    value={bankTransferForm.bankAccount}
                    onChange={(e) => setBankTransferForm({ ...bankTransferForm, bankAccount: e.target.value })}
                    label="Bank Account"
                  >
                    {bankAccounts.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.name} - {bank.accountNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="number"
                  label="Amount (AED)"
                  value={bankTransferForm.amount}
                  onChange={(e) => setBankTransferForm({ ...bankTransferForm, amount: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={bankTransferForm.description}
                  onChange={(e) => setBankTransferForm({ ...bankTransferForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reference (Optional)"
                  value={bankTransferForm.reference}
                  onChange={(e) => setBankTransferForm({ ...bankTransferForm, reference: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankTransferDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleSaveBankTransfer}
            variant="contained"
            color="success"
            disabled={!bankTransferForm.bankAccount || !bankTransferForm.amount || !bankTransferForm.description}
          >
            Record Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThaklisMasterWallet;
